import { useEffect, useEffectEvent, useRef, useState } from 'react';
import clsx from 'clsx';

import { useTranslation } from '@cms/i18n/client';
import { Button } from '@common/atoms/Button';
import styles from '@common/atoms/SearchBox/SearchBox.module.scss';
import { SearchIcon } from '@common/svg/icons/components/SearchIcon';

export interface Props {
    currentSearch: string;
    onSubmit: (SearchText: string) => Promise<boolean> | void;
    hasSearchButton?: boolean;
    autoSubmit?: boolean;
    placeholder?: string;
}

const DEBOUNCE_INTERVAL = 750;

export const SearchBox = ({
    currentSearch,
    onSubmit,
    hasSearchButton,
    autoSubmit = true,
    placeholder,
}: Props) => {
    const __components = useTranslation('components').t;
    const [value, setValue] = useState<string>(currentSearch);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const onDebounceFinished = useEffectEvent((searchValue: string) => {
        if (searchValue !== currentSearch) {
            void onSubmit(searchValue);
        }
    });

    useEffect(() => {
        setValue(currentSearch);
    }, [currentSearch]);

    useEffect(() => {
        if (!autoSubmit) {
            return;
        }
        timerRef.current = setTimeout(() => {
            onDebounceFinished(value);
        }, DEBOUNCE_INTERVAL);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [value, autoSubmit]);

    return (
        <div className={clsx(styles.SearchBox, hasSearchButton && styles['SearchBox--with-button'])}>
            <div className={styles['input-wrapper']}>
                <SearchIcon />
                <input
                    type="search"
                    value={value}
                    name="search"
                    autoComplete="off"
                    placeholder={placeholder ?? __components('SearchBox.placeholder')}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            void onSubmit(value);
                        }
                    }}
                    onChange={(e) => {
                        const newValue = e.target.value;
                        if (newValue !== value) {
                            setValue(newValue);
                        }
                    }}
                />
            </div>
            {hasSearchButton ? (
                <Button
                    variant="filled-primary"
                    className={styles['search-button']}
                    onClick={() => void onSubmit(value)}
                >
                    {__components('SearchBox.search-button')}
                </Button>
            ) : null}
        </div>
    );
};
